/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { HttpClient } from '@angular/common/http';
import { TranslationString } from '../models/translation-string';
import { environment } from '../../environments/environment';
import { LanguageService } from './language.service';
import { UserService } from './user.service';

export interface HasTranslation {
  translations: {
    languages_code: string;
  }[];
}
@Injectable({
  providedIn: 'root',
})
export class TranslationService {
  translationObjects: { [key: string]: any } = {};
  private platformId: number;

  constructor(
    private translate: TranslateService,
    private http: HttpClient,
    private languageService: LanguageService,
    private userService: UserService,
  ) {
    this.platformId = this.userService.user.appInstance.platform;
    this.translate.setDefaultLang('fi');
  }

  /**
   * Use this to manually translate content of a translation object's properties.
   *
   * @param object An array of available translations
   * @returns Translations of the currently selected application language
   */
  getCorrectTranslation<T extends HasTranslation>(object: T): T['translations'][0] {
    let translationObject = object.translations.find(
      (t) =>
        t.languages_code &&
        t.languages_code
          .split('-')[0]
          .toLowerCase()
          .includes(this.languageService.currentLanguage.toLowerCase()),
    );

    if (!translationObject) {
      translationObject = object.translations[0];
    }
    return translationObject;
  }

  /**
   * Gets every translation of the current language. This includes multiple translations for the same language
   * (the PTV data source can have multiple translation objects for the same language). Defaults to finnish when
   * no translation found.
   *
   * @param object An array of available translations
   * @returns All translations of the currently selected application language
   */
  getAllLanguagesTranslations<T extends HasTranslation>(object: T): T['translations'] {
    let translationObjects = object.translations.filter((t) => {
      const exists =
        t.languages_code &&
        t.languages_code
          .split('-')[0]
          .toLowerCase()
          .includes(this.languageService.currentLanguage.toLowerCase());
      return exists;
    });

    if (!translationObjects || translationObjects.length === 0) {
      translationObjects = object.translations.filter((t) => {
        const defaultLanguage = 'fi';
        const exists =
          t.languages_code &&
          t.languages_code.split('-')[0].toLowerCase().includes(defaultLanguage);
        return exists;
      });
    }
    return translationObjects;
  }

  getTranslationFromLanguage<T extends HasTranslation>(
    object: T,
    language: string,
  ): T['translations'][0] {
    let translationObject;
    object.translations.forEach((translation) => {
      if (translation.languages_code.toLowerCase().indexOf(language) >= 0) {
        translationObject = translation;
      }
    });
    if (!translationObject) {
      translationObject = object.translations[0];
    }
    return translationObject;
  }

  getCorrectTranslationKey = (key) => {
    if (key && key.hasOwnProperty(this.translate.currentLang)) {
      return key[this.translate.currentLang];
    }
    if (key && typeof key === 'object') {
      return key[Object.keys(key)[0]];
    } else {
      return key;
    }
  };

  initTranslations = () =>
    this.getTranslations().then(() => {
      const keys = Object.keys(this.translationObjects);
      keys.forEach((key) => {
        if (this.translationObjects.hasOwnProperty(key)) {
          this.translate.setTranslation(key.split('-')[0], this.translationObjects[key], true);
        }
      });
    });

  getTranslations() {
    return this.http
      .get(
        environment.directusBaseUrl +
          'items/translation_strings?fields=name,translations.languages_code,translations.translation' +
          `&filter[platforms][platform_id][_eq]=${this.platformId}` +
          '&limit=-1',
      )
      .toPromise()
      .then((result: { data: TranslationString[] }) => {
        this.generateTranslationObject(result.data);
      });
  }

  // TODO: Find a way to flatten all keys so that the issue below is resolved
  generateTranslationObject = (translationStrings: TranslationString[]) => {
    if (translationStrings && translationStrings.length > 0) {
      translationStrings.forEach((translationString) => {
        if (translationString.translations && translationString.translations.length) {
          translationString.translations.forEach((translation) => {
            if (translation.translation) {
              if (!this.translationObjects[translation.languages_code]) {
                this.translationObjects[translation.languages_code] = {};
              }
              const path = translationString.name.split('.');
              let temporaryObject = this.translationObjects[translation.languages_code];
              path.forEach((pathPart, index) => {
                if (index !== path.length - 1) {
                  if (!temporaryObject[pathPart]) {
                    temporaryObject[pathPart] = {};
                  }
                  temporaryObject = temporaryObject[pathPart];
                } else {
                  // Cannot create an object if the key has an initial string translation value and
                  // another translation uses the same key e.g. "filter: Filter" and "filter.clear: Clear filters"
                  try {
                    temporaryObject[pathPart] = translation.translation;
                  } catch (error) {
                    console.error('Error generating translation object');
                  }
                }
              });
            }
          });
        }
      });
    }
  };
}
